import { Fragment, ReactElement, useContext, useEffect, useState } from "react";
import MenuOption from "./menuOption";
import { IMenuButton, IndicatorType, IState, SchemaNodeType } from "../types";
import { Button, Collapse, Paper, styled, useMediaQuery, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setDataContext, toggleBottomMenu } from "../actions";
import IconButton from "@mui/material/IconButton";
import { getPalette } from "../configuration/configuration";
import MainModule from "../modules/mainModule";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import BarChartIcon from "@mui/icons-material/BarChart";
import { breakpoints } from "../system/theme";
import { SystemContext } from "../context/systemContext";

const Panel = styled("div")(({ theme }) => ({
  height: "60px",
  maxHeight: "60px",
  display: "flex",
  flexDirection: "row",
  overflow: "none",
  [theme.breakpoints.down("sm")]: {
    height: "30px",
    maxHeight: "30px",
  },
  [theme.breakpoints.down("lg")]: {
    height: "40px",
    maxHeight: "40px",
  }
}));

const BottomPanel = (): ReactElement => {
  const dispatch = useDispatch<any>();
  const theme = useTheme();
  const systemContext = useContext(SystemContext);
  const [ stack, setStack ] = useState<IMenuButton[]>([]);
  const [ menu, setMenu ] = useState<IMenuButton[]>([]);
  const [ showMenu, setShowMenu ] = useState<boolean>(false);
  const [ indicators, setIndicators ] = useState<ReactElement[]>([]);
  const bottom = useSelector((state: IState) => state.panels.bottom);
  const dataContext = useSelector((state: IState) => state.dataContext);
  const selectedNode = useSelector((state: IState) => state.selectedNode);
  const matches600 = useMediaQuery(`(min-width:${breakpoints.sm}px)`);
  const isKioskUser = systemContext.user !== null && systemContext.user.role === "KIOSK";

  useEffect(() => {
    setShowMenu(indicators.length === 0);
  }, [matches600, indicators]);

  useEffect(() => {
    if (selectedNode.nodeID !== 0) {
      MainModule.getButtons(selectedNode, isKioskUser).then(moduleMenu => {
        setMenu(moduleMenu);
        setIndicators(MainModule.getIndicators(selectedNode, IndicatorType.BOTTOM));
        setStack([]);

        if (isKioskUser) {
          const defaultMenu = moduleMenu.length > 0
            ? moduleMenu.find(x => x.dataContext.data.isDefault) ?? moduleMenu[0]
            : null;

          if(defaultMenu){
            dispatch(setDataContext(defaultMenu.dataContext));
          }
        } else if (systemContext.user
          && systemContext.user.config.custom
          && systemContext.user.config.custom.defaultNode
          && systemContext.user.config.custom.defaultNode.view) {
          const defaultNode = systemContext.user.config.custom.defaultNode;
          let defaultMenu: IMenuButton | undefined;
          if (selectedNode.type === SchemaNodeType.Factory && selectedNode.nodeID === defaultNode.factory) {
            defaultMenu = moduleMenu.find(x => x.title === defaultNode.view);
          } else if (selectedNode.type === SchemaNodeType.Area && selectedNode.nodeID === defaultNode.area) {
            defaultMenu = moduleMenu.find(x => x.title === defaultNode.view);
          } else if (selectedNode.type === SchemaNodeType.Zone && selectedNode.nodeID === defaultNode.zone) {
            defaultMenu = moduleMenu.find(x => x.title === defaultNode.view);
          }

          if (defaultMenu) {
            dispatch(setDataContext(defaultMenu.dataContext));
          } else if (moduleMenu.length > 0 && moduleMenu[0].dataContext) {
            dispatch(setDataContext(moduleMenu[0].dataContext));
          }
        }
      });
    }
  }, [selectedNode, dispatch, systemContext]);

  const onClickMenu =(option: IMenuButton, back: boolean) => {
    if (back) {
      stack.pop();
      setStack([...stack]);

      if (stack.length === 0) {
        dispatch(setDataContext(menu[0].children ? menu[0].children[0].dataContext : menu[0].dataContext));
      } else {
        const menuStacked = stack[stack.length - 1];
        dispatch(setDataContext(menuStacked.children ? menuStacked.children[0].dataContext : menuStacked.dataContext));
      }

      return;
    }

    if (option.children && option.children.length > 0) {
      stack.push(option);
      setStack([...stack]);

      dispatch(setDataContext(option.children[0].dataContext));
    } else {
      const selected = dataContext?.module === option.dataContext?.module
        && dataContext?.subModule === option.dataContext?.subModule
        && (!dataContext?.data || dataContext.data === option.dataContext.data);

      if (!selected) {
        dispatch(setDataContext(option.dataContext));
      }
    }
  };

  const currentLevel = stack.length > 0  ?  stack[stack.length - 1].children ?? [] : menu;

  const button = {
    verticalAlign: "center",
    margin: "1em 0em"
  };

  const buttonExpand = {
    padding: "0.5em",
    height: "30px",
    marginTop: "1.5em",
    width: "40px",
    minWidth: "40px"
  };

  const buttonMode = {
    padding: "0.5em",
    width: "30px",
    minWidth: "30px"
  };


  return(
    <Collapse orientation="horizontal" in={bottom} collapsedSize={"24px"}>
      <Paper style={{
        position: "absolute",
        background: bottom ? getPalette()[theme.palette.mode].background : "transparent",
        left: bottom ? 0 : "calc(-100% + 24px)",
        bottom: 3,
        width: "100%",
        transition: "left linear .5s"
      }}>
        <Panel style={{ background: "transparent" }}>
          { !matches600 && !showMenu && indicators.length > 0 &&
            <IconButton onClick={() => setShowMenu(true)} size={"small"} style={buttonMode} >
              <MenuIcon fontSize="inherit" fontWeight="bold" />
            </IconButton>
          }

          { !matches600 && showMenu && indicators.length > 0 &&
            <IconButton onClick={() => setShowMenu(false)} size={"small"} style={buttonMode} >
              <BarChartIcon fontSize="inherit" fontWeight="bold" />
            </IconButton>
          }

          {/*Menu area*/}
          { (showMenu || matches600) && <div className="flex-grow-1 scroll-auto h-100 align-content-stretch">
            <div
              className="display-flex-row flex-nowrap align-items-stretch justify-content-start align-content-stretch h-100">
              {stack.length > 0 &&
              <MenuOption option={stack[stack.length - 1]} callback={onClickMenu} back={true} selected={false}/>
              }
              {currentLevel.filter(x => !x.disabled || matches600).map((option: IMenuButton, index: number) => {
                const selected = dataContext?.module === option.dataContext?.module
                  && dataContext?.subModule === option.dataContext?.subModule
                  && dataContext?.title === option.dataContext?.title;

                return (
                  <MenuOption key={`menu-option-${index}`} option={option} callback={onClickMenu} back={false}
                    selected={selected}/>
                );
              })
              }
            </div>
          </div>}

          {/*Indicator area*/}
          { (!showMenu || matches600) &&  <div className={`${ matches600 ? "flex-grow-0" : "flex-grow-1"} h-100 align-content-stretch`} style={{ overflowY: "hidden", overflowX: "auto" }}>
            <div
              className="display-flex-row flex-nowrap align-items-stretch justify-content-start align-content-stretch h-100"
              style={{ overflow: "hidden" }}>
              {indicators.map((component, index) => {
                return <Fragment key={`indicator-${index}`}> {component}</Fragment>;
              })}
            </div>
          </div>}

          {/*Expander button*/}
          {bottom
            ? <IconButton onClick={() => dispatch(toggleBottomMenu())} size={"small"} style={button} >
              <ChevronLeftIcon fontSize="inherit" fontWeight="bold" />
            </IconButton>
            :  <Button color={"primary"} style={buttonExpand}
              variant="contained"
              onClick={() => dispatch(toggleBottomMenu())}
              endIcon={<ChevronRightIcon fontSize="inherit" fontWeight="bold"/>} />
          }
        </Panel>
      </Paper>
    </Collapse>
  );
};

export default BottomPanel;
